import { Helmet } from 'react-helmet-async';
import { Box, Container, Divider, Stack, Typography } from '@mui/material';
import Iconify from '../../components/iconify';

const SECTIONS = [
  {
    phase: 'Feasibility Assessment',
    items: [
      {
        title: 'Boundary & Area',
        content: `1. Obtain preliminary boundary from land owner (KML, shapefile, or parcel maps).
2. Upload boundary to the platform — review on satellite basemap.
3. For each site polygon:
   a. Verify polygon aligns with actual land parcels (cross-reference with cadastral data).
   b. Use "Remove Water Bodies" tool to clip permanent water features (rivers, ponds, channels).
   c. Use "Segment Fields" tool to break into field-level sub-polygons, excluding roads and buildings.
   d. Download corrected boundary, verify offline if needed, re-upload.
4. Record eligible area (ha) per site — total and net (after exclusions).
5. Set each site's phase inclusion: current effort, future expansion, or candidate.
6. Document land use type per site: cropland, grassland, or mixed.`,
      },
      {
        title: 'Eligibility',
        content: `For each site, verify and document all VM0042 Sec 4 applicability conditions:

1. Land Use Type — Confirm cropland or grassland. Not forest, wetland, or peatland.
2. Ownership & Carbon Rights — Collect chain of ownership evidence (title deed, lease, carbon rights agreement).
3. 10-Year No-Clearing — Use satellite imagery (Sentinel-2 time series) to confirm no clearing of natural vegetation in the 10 years prior to project start.
4. Water Bodies — Confirm all water features are delineated and excluded from project area.
5. Infrastructure — Confirm roads, buildings, permanent structures are excluded.
6. Soil Classification — Document soil type (mineral/organic). Confirm not drained peatland or organic soil >30cm.
7. Land Use History — Document baseline scenario with rotation schedule, crop/grazing history, input records.
8. 5% Productivity Rule — Confirm yield has not decreased >5% in recent years.

For each claim: set status (empty → in_progress → done → verified), add notes, attach evidence.`,
      },
    ],
  },
  {
    phase: 'Feasibility Assessment',
    items: [
      {
        title: 'Carbon Potential',
        content: `[To be developed]

Estimate soil carbon removal rate (tCO2e/ha/yr) per site using:
- IPCC Tier 1 default stock change factors by climate zone × soil type × management change
- Optional: CarbonFIX spatial data or site-specific measurements
- Compare quantification approaches: QA1 (model), QA2 (measurement), QA3 (default factors)`,
      },
      {
        title: 'Additionality',
        content: `[To be developed]

Demonstrate project activities would not occur without carbon finance:
- Investment barrier analysis
- Institutional/social/technological barriers
- Common practice assessment for the region
- Regulatory surplus confirmation`,
      },
      {
        title: 'Financial Model',
        content: `[To be developed]

Project economics: area × removal rate × carbon price → revenue projection
- Setup and monitoring costs
- IRR, NPV, payback period over 25-year crediting period
- Sensitivity analysis on carbon price and removal rate`,
      },
    ],
  },
  {
    phase: 'Baseline Establishment',
    items: [
      { title: 'Stratification (VMD0018)', content: '[To be developed]\n\nSatellite layer export → clustering → strata map per site.' },
      { title: 'Sampling Plan', content: '[To be developed]\n\nStratified random sampling design per VM0042 Sec 9.' },
      { title: 'Baseline SOC Measurement', content: '[To be developed]\n\nESM method (VMD0021) — soil cores, lab analysis, carbon stock per stratum.' },
      { title: 'Complete PD + ERR-PD', content: '[To be developed]\n\nFinalize Project Description and projected ERR for VERRA registration.' },
    ],
  },
  {
    phase: 'Monitoring',
    items: [
      { title: 'Annual SOC Re-measurement', content: '[To be developed]' },
      { title: 'Emissions Monitoring', content: '[To be developed]' },
      { title: 'ERR Calculation & MR', content: '[To be developed]' },
    ],
  },
];

export default function SOPPage() {
  return (
    <>
      <Helmet><title>SOP | TERRRA</title></Helmet>
      <Container maxWidth="md" sx={{ py: 2 }}>
        <Typography variant="h5" gutterBottom>Standard Operating Procedures</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Step-by-step procedures for each phase and stage of the VERRA VM0042 project lifecycle.
          This document is a living reference — updated as processes are refined.
        </Typography>

        {SECTIONS.map((section, si) => (
          <Box key={si} sx={{ mb: 4 }}>
            <Typography variant="overline" color="primary" sx={{ display: 'block', mb: 1, fontSize: 13, fontWeight: 700 }}>
              {section.phase}
            </Typography>
            {section.items.map((item, ii) => (
              <Box key={ii} sx={{ mb: 3 }}>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                  <Iconify icon="eva:file-text-outline" sx={{ width: 18, height: 18, color: 'text.secondary' }} />
                  <Typography variant="subtitle1" fontWeight={700}>{section.phase} / {item.title}</Typography>
                </Stack>
                <Box sx={{ ml: 3.5, whiteSpace: 'pre-wrap', fontFamily: 'inherit' }}>
                  <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7 }}>
                    {item.content}
                  </Typography>
                </Box>
              </Box>
            ))}
            {si < SECTIONS.length - 1 && <Divider sx={{ mt: 2 }} />}
          </Box>
        ))}
      </Container>
    </>
  );
}
